import {Template} from "../../../../components/template/template.jsx";
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import EncounterService from "../../../../services/web/encounter-service.js";
import {Card} from "../../../../components/card/card.jsx";
import EncounterIndividualsForm from "../components/encounter-individuals-form.jsx";
import Button from "../../../../components/button/button.jsx";
import React, {useCallback, useEffect, useState} from "react";
import {FaPlus, FaTrash} from "react-icons/fa";
import EncounterVehiclesForm from "../components/encounter-vehicles-form.jsx";
import EncounterJustification from "../components/encounter-justification.jsx";
import EncounterActions from "../components/encounter-actions.jsx";
import ProjectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {normalizeCoordinate} from "../../individual/form/resources/map-utils.js";

const emptyIndividual = {
    cpf: "",
    name: "",
    socialName: "",
    nickname: "",
    mother: "",
    father: "",
    rg: "",
    emitterRg: "",
    criminalRg: "",
    cnh: "",
    dtBirth: null,
    sex: null,
    cityOfBirth: null,
    nationality: null,
    phone: "",
    phoneProvider: null,
    maritalStatus: null,
    mentalDisorder: null,
    alcoholDrugDependency: null,
    height: "",
    color: null,
    bodyShape: null,
    eyes: null,
    hairType: null,
    hairColor: null,
    hairSize: null,
    roleCrime: "",
    situation: null,
    educationDegree: null,
    occupation: "",
    socialNetwork: "",
    information: "",
    address: {
        type: null,
        date: null,
        address: null,
        number: "",
        neighborhood: null,
        city: null,
        complement: "",
        zipCode: "",
        lat: null,
        lng: null,
        notEmpty: false,
    },
    attaches: [],
    homeless: false,
    faceAttach: null,
    documentAttach: null,
};

const emptyVehicle = {
    plate: "",
    brandModel: null,
    type: null,
    color: null,
    fabricationYear: "",
    modelYear: "",
    renavam: "",
    chassis: "",
    engineNumber: "",
    city: null,
    nameOwner: "",
    attaches: [],
    conductor: null,
    passengers: [],
    relatedIndividuals: [],
    captureTime: null,
    documentAttach: null,
    passageAttach: null,
};

const optionValue = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.value ?? null;
    return value;
};

const formatDateToApi = (value) => {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    if (typeof value === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            const [day, month, year] = value.split("/");
            return `${year}-${month}-${day}`;
        }
    }

    return null;
};

const parseDateFromApi = (value) => {
    const normalized = formatDateToApi(value);
    return normalized ? new Date(`${normalized}T00:00:00`) : null;
};

const clearCpf = (value) => String(value ?? "").replace(/\D/g, "");

const findDuplicatedIndividualCpf = (individuals = []) => {
    const cpfMap = new Map();

    for (const [index, individual] of individuals.entries()) {
        const cpf = clearCpf(individual?.cpf);

        if (!cpf) continue;

        if (cpfMap.has(cpf)) {
            return {
                cpf,
                firstIndex: cpfMap.get(cpf),
                secondIndex: index,
            }
        }

        cpfMap.set(cpf, index);
    }
    return null;
}

const getRuntimeIndividualKey = (individual, index = 0) =>
    individual?.id ?? individual?._tempKey ?? individual?.cpf ?? `individual-${index}`;

const getExtensionFromName = (name = "") => {
    const parts = String(name).split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const normalizeDirectAttach = (file, normalizedAttaches, requestData) => {
    if (!(file instanceof File)) {
        if (typeof file === "object" && file !== null) {
            return file.attachId ?? file.attach_id ?? file.id ?? null;
        }
        return file ?? null;
    }

    const name = file.name;
    const attachId = getLegacyAttachId(name);

    requestData.append("attaches", file, name);

    const exists = normalizedAttaches.some((item) => item.attachId === attachId);

    if (!exists) {
        normalizedAttaches.push({
            name,
            attachId,
            extension: getExtensionFromName(name),
        });
    }

    return attachId;
};

const normalizeAttaches = (attaches = [], requestData) =>
    attaches.reduce((acc, attach) => {
        if (attach instanceof File) {
            const attachId = getLegacyAttachId(attach.name);

            requestData.append("attaches", attach);

            acc.push({
                name: attach.name,
                attachId,
                extension: getExtensionFromName(attach.name),
            });

            return acc;
        }

        if (attach?.attachId) {
            acc.push({
                name: attach?.name ?? attach.attachId,
                attachId: attach.attachId,
                extension: attach?.extension ?? getExtensionFromName(attach?.name),
            });
        }

        return acc;
    }, []);

const hasTextValue = (value) => {
    const normalized = optionValue(value);
    if (normalized == null) return false;
    return String(normalized).trim() !== "";
};

const numberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;

    const normalized = Number(value);

    return Number.isNaN(normalized) ? null : normalized;
};

const integerOrNull = (value) => {
    const normalized = numberOrNull(value);
    return Number.isInteger(normalized) ? normalized : null;
};

const isIndividualFilled = (individual) => {
    const hasMainFields = [
        individual?.cpf,
        individual?.name,
        individual?.socialName,
        individual?.nickname,
        individual?.mother,
        individual?.father,
        individual?.rg,
        individual?.emitterRg,
        individual?.criminalRg,
        individual?.cnh,
        individual?.sex,
        individual?.cityOfBirth,
        individual?.nationality,
        individual?.phone,
        individual?.phoneProvider,
        individual?.maritalStatus,
        individual?.color,
        individual?.bodyShape,
        individual?.eyes,
        individual?.hairType,
        individual?.hairColor,
        individual?.hairSize,
        individual?.roleCrime,
        individual?.situation,
        individual?.educationDegree,
        individual?.occupation,
        individual?.socialNetwork,
        individual?.information,
        individual?.faceAttach,
        individual?.documentAttach,
    ].some(hasTextValue);

    const hasDate = Boolean(formatDateToApi(individual?.dtBirth));
    const hasHeight = integerOrNull(individual?.height) !== null;
    const hasExplicitBoolean = individual?.mentalDisorder === true || individual?.alcoholDrugDependency === true;

    const hasAddress =
        Boolean(individual?.address?.notEmpty) ||
        Boolean(formatDateToApi(individual?.address?.date)) ||
        normalizeCoordinate(individual?.address?.lat) !== null ||
        normalizeCoordinate(individual?.address?.lng) !== null ||
        [
            individual?.address?.type,
            individual?.address?.address,
            individual?.address?.number,
            individual?.address?.neighborhood,
            individual?.address?.city,
            individual?.address?.complement,
            individual?.address?.zipCode,
        ].some(hasTextValue);

    const hasAttaches = (individual?.attaches ?? []).length > 0;
    const hasPassthroughData =
        (individual?.individualRegionWrapper ?? []).length > 0 ||
        (individual?.articles ?? []).length > 0;

    return hasMainFields || hasDate || hasHeight || hasExplicitBoolean || hasAddress || hasAttaches || hasPassthroughData;
};

const getLegacyAttachId = (name = "") => {
    const parts = String(name).split(".");
    if (parts.length <= 1) return name;
    parts.pop();
    return parts.join(".");
};

const normalizeSexOption = (value) => {
    if (!value) return null;

    const sex = String(value);

    if (sex === "MASCULINO" || sex === "MALE") return {value: "MALE", label: "MASCULINO"};
    if (sex === "FEMININO" || sex === "FEMALE") return {value: "FEMALE", label: "FEMININO"};

    return null;
};

const normalizeIndividualToApi = (individual, index, requestData) => {
    const normalizedAttaches = normalizeAttaches(individual?.attaches ?? [], requestData);

    return {
        id: index + 1,
        cpf: clearCpf(individual?.cpf),
        name: individual?.name ?? "",
        socialName: individual?.socialName ?? "",
        nickname: individual?.nickname ?? "",
        mother: individual?.mother ?? "",
        father: individual?.father ?? "",
        rg: individual?.rg ?? "",
        emitterRg: individual?.emitterRg ?? "",
        criminalRg: individual?.criminalRg ?? "",
        cnh: individual?.cnh ?? "",
        dtBirth: formatDateToApi(individual?.dtBirth),
        sex: optionValue(individual?.sex),
        cityOfBirth: optionValue(individual?.cityOfBirth),
        nationality: optionValue(individual?.nationality),
        phone: individual?.phone ?? null,
        phoneProvider: individual?.phoneProvider ?? null,
        maritalStatus: optionValue(individual?.maritalStatus),
        mentalDisorder: individual?.mentalDisorder ?? null,
        alcoholDrugDependency: individual?.alcoholDrugDependency ?? null,
        height: integerOrNull(individual?.height),
        color: optionValue(individual?.color),
        bodyShape: optionValue(individual?.bodyShape),
        eyes: optionValue(individual?.eyes),
        hairType: optionValue(individual?.hairType),
        hairColor: optionValue(individual?.hairColor),
        hairSize: optionValue(individual?.hairSize),
        roleCrime: individual?.roleCrime ?? null,
        situation: optionValue(individual?.situation),
        educationDegree: optionValue(individual?.educationDegree),
        occupation: individual?.occupation ?? null,
        socialNetwork: individual?.socialNetwork ?? null,
        information: individual?.information ?? null,
        individualRegionWrapper: individual?.individualRegionWrapper,
        articles: Array.isArray(individual?.articles)
            ? individual.articles.map((article) => ({
                itemId: article?.itemId,
                date: formatDateToApi(article?.date),
                bo: article?.bo ?? null,
                article: optionValue(article?.article),
                attachId: article?.attachId ?? null,
            }))
            : individual?.articles,
        deletedAttaches: individual?.deletedAttaches,
        homeless: individual?.address?.notEmpty ?? individual?.homeless ?? false,
        address: {
            type: optionValue(individual?.address?.type),
            date: formatDateToApi(individual?.address?.date),
            address: optionValue(individual?.address?.address),
            number: individual?.address?.number ?? "",
            neighborhood: optionValue(individual?.address?.neighborhood),
            city: optionValue(individual?.address?.city),
            complement: individual?.address?.complement ?? "",
            zipCode: individual?.address?.zipCode ?? "",
            lat: normalizeCoordinate(individual?.address?.lat),
            lng: normalizeCoordinate(individual?.address?.lng),
            notEmpty: individual?.address?.notEmpty ?? individual?.homeless ?? false,
        },
        attaches: normalizedAttaches,
        faceAttach: normalizeDirectAttach(individual?.faceAttach, normalizedAttaches, requestData),
        documentAttach: normalizeDirectAttach(individual?.documentAttach, normalizedAttaches, requestData),
    };
};

const normalizeVehicleToApi = (vehicle, index, individualIdMap, requestData) => {
    const relatedIndividuals = [
        ...(vehicle?.conductor?.value
            ? [{
                individualId:
                    individualIdMap.get(vehicle.conductor.value) ?? vehicle.conductor.value,
                relationship: "DRIVER",
            }]
            : []),
        ...((vehicle?.passengers ?? []).map((passenger) => ({
            individualId:
                individualIdMap.get(passenger.value) ?? passenger.value,
            relationship: "PASSENGER",
        }))),
    ];

    return {
        id: index + 1,
        plate: String(vehicle?.plate ?? "")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase(),
        brandModel: optionValue(vehicle?.brandModel),
        type: optionValue(vehicle?.type),
        color: optionValue(vehicle?.color),
        fabricationYear: numberOrNull(vehicle?.fabricationYear),
        modelYear: numberOrNull(vehicle?.modelYear),
        renavam: vehicle?.renavam ?? "",
        chassis: vehicle?.chassis ?? "",
        engineNumber: vehicle?.engineNumber ?? "",
        city: optionValue(vehicle?.city),
        nameOwner: vehicle?.nameOwner ?? "",
        attaches: normalizeAttaches(vehicle?.attaches ?? [], requestData),
        relatedIndividuals,
        captureTime: vehicle?.captureTime ?? null,
        documentAttach: vehicle?.documentAttach ?? null,
        passageAttach: vehicle?.passageAttach ?? null,
    };
};

const collectOldAttachesId = (individuals = [], vehicles = []) => {
    const ids = new Set();

    const addAttach = (attach) => {
        const isNewFile = attach instanceof File && attach?.new;
        if (!isNewFile && attach?.attachId) {
            ids.add(attach.attachId);
        }
    };

    individuals.forEach((individual) => {
        (individual?.attaches ?? []).forEach(addAttach);

        const isNewIndividual = Boolean(individual?._tempKey);

        if (!isNewIndividual && individual?.faceAttach && !(individual.faceAttach instanceof File)) {
            ids.add(individual.faceAttach);
        }

        if (!isNewIndividual && individual?.documentAttach && !(individual.documentAttach instanceof File)) {
            ids.add(individual.documentAttach);
        }
    });

    vehicles.forEach((vehicle) => {
        (vehicle?.attaches ?? []).forEach(addAttach);

        if (vehicle?.documentAttach && !(vehicle.documentAttach instanceof File)) {
            ids.add(vehicle.documentAttach);
        }

        if (vehicle?.passageAttach && !(vehicle.passageAttach instanceof File)) {
            ids.add(vehicle.passageAttach);
        }
    });

    return Array.from(ids).join(",");
};

const toOption = (value) => {
    if (!value) return null;
    return { value, label: value };
};

const getIndividualOption = (individual, index = 0) => ({
    value: individual?.id ?? individual?._tempKey ?? individual?.cpf ?? index,
    label: individual?.name
        ? individual.name
        : individual?.cpf
            ? ProjectUtils.formatCpfToView(individual.cpf)
            : `Indivíduo ${index + 1}`,
});

const normalizeVehicleFromApi = (vehicle, individuals = []) => {
    const relatedIndividuals = vehicle?.relatedIndividuals ?? [];

    const findIndividualOption = (individualId) => {
        const found = individuals.find((individual, index) => {
            const key = individual?.id ?? individual?._tempKey ?? individual?.cpf ?? index;
            return key === individualId || individual?.id === individualId;
        });

        if (!found) return null;

        return getIndividualOption(found);
    };

    const conductorRelation = relatedIndividuals.find(
        (item) => item.relationship === "DRIVER"
    );

    const passengerRelations = relatedIndividuals.filter(
        (item) => item.relationship === "PASSENGER"
    );

    return {
        ...vehicle,
        brandModel: toOption(vehicle?.brandModel),
        type: toOption(vehicle?.type),
        color: toOption(vehicle?.color),
        city: toOption(vehicle?.city),
        conductor: conductorRelation
            ? findIndividualOption(conductorRelation.individualId)
            : null,
        passengers: passengerRelations
            .map((item) => findIndividualOption(item.individualId))
            .filter(Boolean),
        attaches: vehicle?.attaches ?? [],
    };
};

const createTempKey = (prefix, index) =>
    crypto.randomUUID?.() ?? `${prefix}-${Date.now()}-${index}`;

const getItemKey = (item, fallback) =>
    item?.id ?? item?._tempKey ?? item?.cpf ?? fallback;

const updateItemByKey = (list, itemKey, updated) =>
    list.map((item, index) =>
        getItemKey(item, index) === itemKey ? updated : item
    );

const removeItemByTempKey = (list, tempKey) =>
    list.filter((item) => item?._tempKey !== tempKey);

export default function EncounterEdit(){
    const navigate = navigateHook();
    const { id } = useParams();
    const [individualForms, setIndividualForms] = useState([]);
    const [vehicleForms, setVehicleForms] = useState([]);
    const [formData, setFormData] = useState({
        observation: "",
        actions: [],
    });

    const { data, isLoading } = useQuery({
        queryKey: ["encounter-id", id],
        queryFn: () => EncounterService.getEncounter(id),
    });

    const { data: actions } = useQuery({
        queryKey: ["encounter-actions", id],
        queryFn: () => EncounterService.getEncounterActions(),
    });

    const normalizeIndividualFromApi = (individual) => ({
        ...individual,
        dtBirth: parseDateFromApi(individual?.dtBirth),
        sex: normalizeSexOption(individual?.sex),
        cityOfBirth: toOption(individual?.cityOfBirth),
        nationality: toOption(individual?.nationality),
        phone: individual?.phone ?? null,
        phoneProvider: individual?.phoneProvider ?? null,
        maritalStatus: individual?.maritalStatus ?? null,
        mentalDisorder: individual?.mentalDisorder ?? null,
        alcoholDrugDependency: individual?.alcoholDrugDependency ?? null,
        height: individual?.height ?? "",
        color: individual?.color ?? null,
        bodyShape: individual?.bodyShape ?? null,
        eyes: individual?.eyes ?? null,
        hairType: individual?.hairType ?? null,
        hairColor: individual?.hairColor ?? null,
        hairSize: individual?.hairSize ?? null,
        roleCrime: individual?.roleCrime ?? null,
        situation: individual?.situation ?? null,
        educationDegree: individual?.educationDegree ?? null,
        occupation: individual?.occupation ?? null,
        socialNetwork: individual?.socialNetwork ?? null,
        information: individual?.information ?? null,
        address: {
            type: individual?.address?.type ?? null,
            date: parseDateFromApi(individual?.address?.date),
            address: toOption(individual?.address?.address),
            number: individual?.address?.number ?? "",
            neighborhood: toOption(individual?.address?.neighborhood),
            city: toOption(individual?.address?.city),
            complement: individual?.address?.complement ?? "",
            zipCode: individual?.address?.zipCode ?? "",
            lat: individual?.address?.lat ?? null,
            lng: individual?.address?.lng ?? null,
            notEmpty: individual?.homeless ?? false,
        },
        attaches: individual?.attaches ?? [],
        homeless: individual?.homeless ?? false,
    });

    useEffect(() => {
        const encounter = data?.data?.encounter?.data;
        if (!encounter) return;

        const normalizedIndividuals = (encounter.individuals ?? []).map(normalizeIndividualFromApi);
        const normalizedVehicles = (encounter.vehicles ?? []).map((vehicle) =>
            normalizeVehicleFromApi(vehicle, normalizedIndividuals)
        );

        setIndividualForms(normalizedIndividuals);
        setVehicleForms(normalizedVehicles);
        setFormData({
            observation: encounter.observation ?? "",
            actions: encounter.actions ?? [],
        });
    }, [data]);

    const handleAddIndividual = useCallback(() =>
            setIndividualForms((prev) => [
                ...prev,
                { ...emptyIndividual, _tempKey: createTempKey("individual", prev.length) },
            ]),
        []);

    const handleAddVehicle = useCallback(() =>
            setVehicleForms((prev) => [
                ...prev,
                { ...emptyVehicle, _tempKey: createTempKey("vehicle", prev.length) },
            ]),
        []);

    const handleRemoveIndividual = useCallback((tempKey) =>
            setIndividualForms((prev) => removeItemByTempKey(prev, tempKey)),
        []);

    const handleRemoveVehicle = useCallback((tempKey) =>
            setVehicleForms((prev) => removeItemByTempKey(prev, tempKey)),
        []);

    const mergeIndividualPatch = (current, patch) => {
        return {
            ...(current ?? {}),
            ...(patch ?? {}),
            address: patch?.address
                ? {
                    ...(current?.address ?? {}),
                    ...(patch.address ?? {}),
                }
                : current?.address,
        };
    };

    const handleIndividualChange = useCallback((formKey, patch) =>
            setIndividualForms((prev) => {
                let changed = false;

                const next = prev.map((item, index) => {
                    const itemKey = getItemKey(item, index);

                    if (itemKey !== formKey) return item;

                    const updated = mergeIndividualPatch(item, patch);

                    const sameData = JSON.stringify(item) === JSON.stringify(updated);
                    if (sameData) return item;

                    changed = true;
                    return updated;
                });

                return changed ? next : prev;
            }),
        []);

    const handleVehicleChange = useCallback((formKey, updatedData) =>
            setVehicleForms((prev) => updateItemByKey(prev, formKey, updatedData)),
        []);

    const handleSave = async () => {
        const canEdit = data?.data?.can_edit;
        if (!canEdit) return;

        const observation = String(formData?.observation ?? "").trim();

        if (!observation) {
            ProjectUtils.swalErrorMessage("", "É necessário preencher uma justificativa para encerrar a abordagem.", "Ok");
            return;
        }

        const requestData = new FormData();
        const filledIndividuals = (individualForms ?? []).filter(isIndividualFilled);
        const duplicatedIndividualCpf = findDuplicatedIndividualCpf(filledIndividuals);

        if (duplicatedIndividualCpf) {
            ProjectUtils.swalErrorMessage(
                "",
                `Já existe um indivíduo cadastrado com o CPF ${ProjectUtils.formatCpfToView(duplicatedIndividualCpf.cpf)}.`,
                "Ok"
            );
            return;
        }

        const individualIdMap = new Map();

        const normalizedIndividuals = filledIndividuals.map((individual, index) => {
            const newId = index + 1;
            const runtimeKey = getRuntimeIndividualKey(individual, index);

            individualIdMap.set(runtimeKey, newId);
            if (individual?.id != null) individualIdMap.set(individual.id, newId);
            if (individual?._tempKey) individualIdMap.set(individual._tempKey, newId);

            return normalizeIndividualToApi(individual, index, requestData);
        });

        const normalizedVehicles = (vehicleForms ?? []).map((vehicle, index) => normalizeVehicleToApi(vehicle, index, individualIdMap, requestData));

        const hasVehicleWithoutPlate = normalizedVehicles.some((vehicle) => !vehicle.plate);

        if (hasVehicleWithoutPlate) {
            ProjectUtils.swalErrorMessage("", "Há veículos com o campo placa vazio.", "Ok");
            return;
        }

        const normalizedActions = (formData?.actions ?? []).map((action) => ({ id: action.id, text: action.text }));

        const encounterPayload = {
            ...(data?.data?.encounter ?? {}),
            data: {
                ...(data?.data?.encounter?.data ?? {}),
                observation,
                actions: normalizedActions,
                individuals: normalizedIndividuals,
                vehicles: normalizedVehicles,
            },
        };

        const oldAttachesId = collectOldAttachesId(individualForms, vehicleForms);

        requestData.append(
            "encounter",
            new Blob([JSON.stringify(encounterPayload)], {
                type: "application/json",
            })
        );

        requestData.append("oldAttachesId", oldAttachesId);

        try {
            await EncounterService.update(requestData);
            ProjectUtils.success(true);
            navigate(`/encounter/form/${id}`);
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.message ?? error?.response?.data?.error ?? "Não foi possível salvar.";
            ProjectUtils.swalErrorMessage("", msg, "Ok");
        }
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Abordagem - {id}</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Header>
                        <Card.Title>Indivíduos</Card.Title>
                    </Card.Header>
                    <Card.Body className="space-y-3">
                        {individualForms.map((individual, index) => {
                            const formKey = individual?.id ?? individual?._tempKey ?? individual?.cpf ?? index;
                            const isNew = Boolean(individual?._tempKey);
                            return (
                                <div key={`individual-wrapper-${formKey}`} className="space-y-2 relative">
                                    {isNew ? (
                                    <div className="absolute -right-2 -top-2.5 z-10">
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveIndividual(individual._tempKey)}
                                            className="inline-flex items-center justify-center rounded-md bg-red-700 p-2 text-white hover:bg-red-600 cursor-pointer"
                                        >
                                            <FaTrash className="text-sm" />
                                        </button>
                                    </div>
                                    ) : null}
                                <EncounterIndividualsForm
                                    formKey={formKey}
                                    data={individual}
                                    onChange={(updated) => handleIndividualChange(formKey, updated)}
                                />
                                </div>
                            );
                        })}
                        <div className="flex gap-2 items-start">
                            <Button onClick={handleAddIndividual}><FaPlus/> Adicionar indivíduo</Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title>Veículos</Card.Title>
                    </Card.Header>
                    <Card.Body className="space-y-3">
                        {vehicleForms.map((vehicle, index) => {
                            const formKey = vehicle?.id ?? vehicle?._tempKey ?? vehicle?.plate ?? index;
                            const isNew = Boolean(vehicle?._tempKey);
                            return (
                                <div key={`vehicle-wrapper-${formKey}`} className="space-y-2 relative">
                                    {isNew ? (
                                        <div className="absolute -right-2 -top-2.5 z-10">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveVehicle(vehicle._tempKey)}
                                                className="inline-flex items-center justify-center rounded-md bg-red-700 p-2 text-white hover:bg-red-600 cursor-pointer"
                                            >
                                                <FaTrash className="text-sm" />
                                            </button>
                                        </div>
                                    ) : null}
                                    <EncounterVehiclesForm
                                        formKey={formKey}
                                        data={vehicle}
                                        individuals={individualForms}
                                        allVehicles={vehicleForms}
                                        onChange={(updated) => handleVehicleChange(formKey, updated)}
                                    />
                                </div>
                            )
                        })}
                        <div className="flex gap-2 items-start">
                            <Button onClick={handleAddVehicle}><FaPlus/> Adicionar veículo</Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title>Justificativa *</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <EncounterJustification
                            data={formData.observation}
                            onChange={(value) => setFormData((prev) => ({ ...prev, observation: value}))}
                        />
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Header>
                        <Card.Title>Ações decorrentes</Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <EncounterActions
                            encounterActions={actions}
                            selectedActions={formData.actions}
                            onChange={(actions) => setFormData((prev) => ({ ...prev, actions }))}
                        />
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        <Button onClick={handleSave} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}
